import React, { useEffect, useState } from 'react';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { PaymentRequest } from '@stripe/stripe-js';
import { CircularProgress } from '@mui/material';
import { doFetch } from '@/util/fetch';
import { apiServer } from '@/util/apiServer';
import { getIdToken } from '@/util/authentication';

interface StripePaymentRequestButtonProps {
    amount: number;
    label: string;
    type: 'apple' | 'google';
    onSuccess: () => void;
    logo: string;
    clientSecret: string;
    orderUuid: string;
    beforePayment?: () => Promise<boolean>;
    finalizePaymentIntent?: (paymentIntentId: string) => Promise<boolean>;
}

const FINALIZATION_MAX_ATTEMPTS = 4;
const FINALIZATION_RETRY_DELAY_MS = 500;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Renders Stripe Payment Request Button (Apple Pay / Google Pay).
 * Falls back to a disabled button with explanatory message on unsupported devices.
 * 
 * When supported:
 *   1. User clicks official Stripe button
 *   2. Stripe shows native payment sheet
 *   3. On paymentmethod event: calls stripe.confirmPayment with clientSecret
 *   4. On confirmation success: calls onSuccess()
 * 
 * When unsupported (e.g., Windows for Apple Pay):
 *   - Displays a grayed-out button with "Not available on this device" message
 *   - Button is always visible (satisfies the requirement)
 */
export default function StripePaymentRequestButton({
    amount,
    label,
    type,
    onSuccess,
    logo,
    clientSecret,
    orderUuid,
    beforePayment,
    finalizePaymentIntent,
}: StripePaymentRequestButtonProps) {
    const stripe = useStripe();
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
    const [canMakePayment, setCanMakePayment] = useState<boolean | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const finalizeOrderPayment = async (paymentIntentId: string): Promise<boolean> => {
        if (!orderUuid || !paymentIntentId) return false;

        const token = (await getIdToken())?.replace(/"/g, '') ?? '';

        const attemptFinalization = async (): Promise<boolean> => {
            if (token) {
                try {
                    const response = await doFetch(`${apiServer ?? ''}/api/orders/new/${encodeURIComponent(orderUuid)}/action/post-payment/stripe`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ paymentIntentId }),
                    });

                    if (response.ok) {
                        return true;
                    }
                } catch (error) {
                    console.warn('Authenticated post-payment finalization failed:', error);
                }
            }

            try {
                const response = await doFetch(`${apiServer ?? ''}/api/orders/new/${encodeURIComponent(orderUuid)}/action/post-payment/stripe/noauth?paymentIntentId=${encodeURIComponent(paymentIntentId)}`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' },
                });

                return response.ok;
            } catch (error) {
                console.warn('Unauthenticated post-payment finalization failed:', error);
                return false;
            }
        };

        for (let attempt = 1; attempt <= FINALIZATION_MAX_ATTEMPTS; attempt++) {
            const finalized = await attemptFinalization();
            if (finalized) return true;

            if (attempt < FINALIZATION_MAX_ATTEMPTS) {
                await sleep(FINALIZATION_RETRY_DELAY_MS);
            }
        }

        return false;
    };

    const finalizePayment = async (paymentIntentId: string): Promise<boolean> => {
        if (finalizePaymentIntent) {
            return await finalizePaymentIntent(paymentIntentId);
        }

        return await finalizeOrderPayment(paymentIntentId);
    };

    useEffect(() => {
        if (!stripe) return;

        const pr = stripe.paymentRequest({
            country: 'AU',
            currency: 'aud',
            total: {
                label: label,
                amount: amount,
            },
            requestPayerName: true,
            requestPayerEmail: true,
        });

        pr.canMakePayment().then((result) => {
            if (type === 'apple') {
                setCanMakePayment(!!result?.applePay);
                return;
            }

            setCanMakePayment(!!result);
        });

        (pr as unknown as { on: (eventType: string, handler: (event: { preventDefault?: () => void }) => void | Promise<void>) => void }).on('click', async (event) => {
            const canProceed = beforePayment ? await beforePayment() : true;

            if (!canProceed) {
                event.preventDefault?.();
            }
        });

        pr.on('paymentmethod', async (ev) => {
            setIsProcessing(true);
            setErrorMessage(null);

            try {
                const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: ev.paymentMethod.id,
                });

                if (error) {
                    setErrorMessage(error.message || "Payment failed. Please try again.");
                    ev.complete('fail');
                    setIsProcessing(false);
                    return;
                }

                if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
                    ev.complete('success');

                    if (paymentIntent?.status === 'succeeded') {
                        const finalized = await finalizePayment(paymentIntent.id);
                        if (!finalized) {
                            setErrorMessage('Payment succeeded, but order finalization is still pending. Please refresh in a few seconds.');
                            setIsProcessing(false);
                            return;
                        }

                        onSuccess();
                        return;
                    }

                    setErrorMessage('Payment is processing. Please wait a few moments and refresh this page to confirm order status.');
                    setIsProcessing(false);
                    return;
                } else {
                    setErrorMessage("Payment confirmation failed. Please try again.");
                    ev.complete('fail');
                    setIsProcessing(false);
                }
            } catch (err: any) {
                console.error('Payment request error:', err);
                setErrorMessage(err.message || "An error occurred. Please try again.");
                ev.complete('fail');
                setIsProcessing(false);
            }
        });

        setPaymentRequest(pr);

        return () => {};
    }, [stripe, amount, label, clientSecret, onSuccess, beforePayment, orderUuid]);

    if (!paymentRequest || canMakePayment === null) {
        return (
            <div className="flex items-center justify-center h-12 w-full bg-gray-200 rounded animate-pulse">
                <CircularProgress size={24} />
            </div>
        );
    }

    if (canMakePayment) {
        return (
            <div className="w-full">
                {errorMessage && (
                    <div className="mb-3 p-3 bg-red-100 border border-red-400 rounded text-red-800 text-sm">
                        {errorMessage}
                    </div>
                )}
                <div className="opacity-50 pointer-events-none" style={{ pointerEvents: isProcessing ? 'none' : 'auto', opacity: isProcessing ? 0.7 : 1 }}>
                    <PaymentRequestButtonElement
                        options={{
                            paymentRequest: paymentRequest,
                        }}
                    />
                </div>
            </div>
        );
    }

    const deviceMessage = type === 'apple'
        ? "Apple Pay is only available on Safari (macOS/iOS)."
        : "Google Pay requires a supported browser or Android device.";

    if (type === 'apple') {
        return (
            <div className="w-full">
                {errorMessage && (
                    <div className="mb-3 p-3 bg-red-100 border border-red-400 rounded text-red-800 text-sm">
                        {errorMessage}
                    </div>
                )}
                <p className="text-sm text-center text-gray-500 mt-2">
                    {deviceMessage}
                </p>
            </div>
        );
    }

    return (
        <div className="w-full">
            {errorMessage && (
                <div className="mb-3 p-3 bg-red-100 border border-red-400 rounded text-red-800 text-sm">
                    {errorMessage}
                </div>
            )}
            <button
                disabled
                className="w-full flex items-center justify-center gap-3 bg-gray-900 border border-gray-700 rounded h-[48px] cursor-not-allowed opacity-60 hover:opacity-60 transition-opacity"
                type="button"
            >
                <img src={logo} alt={`${type} Pay`} className="h-6 object-contain grayscale opacity-70" />
                <span className="text-gray-400 font-medium text-sm">Not available on this device</span>
            </button>
            <p className="text-xs text-center text-gray-500 mt-2">
                {deviceMessage}
            </p>
        </div>
    );
}
